import { useRef } from "react";
import { Check, ImagePlus, X } from "lucide-react";
import { bookingServices } from "@/data/site";
import { Calendar, formatCH } from "@/components/booking/Calendar";
import { cn } from "@/lib/utils";

export const Field = ({ label, name, value, onChange, error, type = "text", placeholder, required, inputMode, autoComplete }) => (
  <label className="block">
    <span className="eyebrow">{label}{required && <span className="text-[color:var(--violet)]"> *</span>}</span>
    <input type={type} name={name} value={value} onChange={(e) => onChange(name, e.target.value)} placeholder={placeholder} inputMode={inputMode} autoComplete={autoComplete} aria-invalid={!!error} data-testid={`booking-input-${name}`} className={cn("field mt-1", error && "field-error")} />
    {error && <span role="alert" className="block mt-1.5 text-[13px] text-red-600" data-testid={`booking-error-${name}`}>{error}</span>}
  </label>
);

export const StepVehicle = ({ d, set, errors }) => (
  <div className="grid sm:grid-cols-2 gap-x-8 gap-y-8">
    <Field label="Marke" name="brand" value={d.brand} onChange={set} error={errors.brand} placeholder="z. B. Audi" required />
    <Field label="Modell" name="model" value={d.model} onChange={set} error={errors.model} placeholder="z. B. A4 Avant" required />
    <Field label="Jahrgang" name="year" value={d.year} onChange={set} placeholder="z. B. 2019" inputMode="numeric" />
    <Field label="Kilometerstand" name="mileage" value={d.mileage} onChange={set} placeholder="z. B. 84'000" inputMode="numeric" />
    <Field label="Kontrollschild" name="plate" value={d.plate} onChange={set} placeholder="z. B. ZH 123 456" />
  </div>
);

export const StepService = ({ d, set, errors }) => (
  <div>
    <div className="grid sm:grid-cols-2 gap-3" role="radiogroup" aria-label="Service wählen">
      {bookingServices.map((s) => (
        <button key={s} type="button" role="radio" aria-checked={d.service === s} data-testid={`booking-service-${s.toLowerCase().replace(/\s/g, "-")}`} onClick={() => set("service", s)}
          className={cn("flex items-center justify-between rounded-xl border px-5 py-4 min-h-[60px] text-left text-[17px] transition-[border-color,background-color] duration-300", d.service === s ? "border-[color:var(--plum)] bg-[color:var(--plum)] text-white" : "hairline bg-[color:var(--surface)] hover:border-[color:var(--ink-3)]")}>
          {s}
          <span className={cn("h-5 w-5 rounded-full border flex items-center justify-center", d.service === s ? "border-white bg-white text-[color:var(--plum)]" : "hairline")}>{d.service === s && <Check className="h-3 w-3" strokeWidth={3} />}</span>
        </button>
      ))}
    </div>
    {errors.service && <p role="alert" className="mt-3 text-[13px] text-red-600" data-testid="booking-error-service">{errors.service}</p>}
  </div>
);

export const StepProblem = ({ d, set, files, setFiles }) => {
  const inputRef = useRef(null);
  const add = (list) => {
    const imgs = Array.from(list).filter((f) => f.type.startsWith("image/") && f.size <= 5 * 1024 * 1024);
    setFiles((cur) => [...cur, ...imgs].slice(0, 4));
  };
  return (
    <div className="space-y-10">
      <label className="block">
        <span className="eyebrow">Was können wir für Sie tun?</span>
        <textarea name="message" rows={5} value={d.message} onChange={(e) => set("message", e.target.value)} data-testid="booking-input-message" placeholder="Beschreiben Sie das Anliegen – Geräusche, Warnleuchten, Situation, seit wann." className="field mt-1 resize-none leading-relaxed" />
      </label>
      <div>
        <p className="eyebrow mb-3">Fotos (optional)</p>
        <div className="flex flex-wrap gap-3">
          {files.map((f, i) => (
            <div key={`${f.name}-${i}`} className="relative h-24 w-24 rounded-xl overflow-hidden border hairline" data-testid={`booking-photo-${i}`}>
              <img src={URL.createObjectURL(f)} alt={f.name} className="h-full w-full object-cover" />
              <button type="button" aria-label={`${f.name} entfernen`} onClick={() => setFiles((cur) => cur.filter((_, j) => j !== i))} className="absolute top-1 right-1 h-6 w-6 rounded-full bg-black/70 text-white flex items-center justify-center"><X className="h-3 w-3" /></button>
            </div>
          ))}
          {files.length < 4 && (
            <button type="button" onClick={() => inputRef.current?.click()} data-testid="booking-photo-add" onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); add(e.dataTransfer.files); }}
              className="h-24 min-w-[160px] flex-1 rounded-xl border border-dashed hairline flex items-center justify-center gap-2 text-[14px] text-[color:var(--ink-2)] hover:border-[color:var(--ink)] hover:text-[color:var(--ink)] transition-colors">
              <ImagePlus className="h-4 w-4" strokeWidth={1.75} /> Foto hinzufügen
            </button>
          )}
          <input ref={inputRef} type="file" accept="image/*" multiple className="sr-only" data-testid="booking-photo-input" onChange={(e) => { add(e.target.files); e.target.value = ""; }} />
        </div>
        <p className="mt-3 text-[13px] text-[color:var(--ink-3)]">Warnleuchten, Schäden, Reifen, Bauteile. Max. 4 Bilder, je bis 5 MB.</p>
      </div>
    </div>
  );
};

export const StepDate = ({ d, set, errors }) => (
  <div className="grid lg:grid-cols-[auto_1fr] gap-10 items-start">
    <Calendar value={d.date} onChange={(v) => set("date", v)} />
    <div>
      <p className="eyebrow mb-3">Bevorzugte Zeit</p>
      <div className="flex flex-wrap gap-2.5" role="radiogroup" aria-label="Zeitfenster">
        {["Vormittag", "Nachmittag", "Flexibel"].map((t) => (
          <button key={t} type="button" role="radio" aria-checked={d.time === t} data-active={d.time === t} data-testid={`booking-time-${t.toLowerCase()}`} onClick={() => set("time", t)} className="chip">{t}</button>
        ))}
      </div>
      {d.date && <p className="mt-8 text-[22px] font-medium tracking-[-0.02em]" data-testid="booking-date-preview">{formatCH(d.date)}{d.time ? `, ${d.time}` : ""}</p>}
      {errors.date && <p role="alert" className="mt-3 text-[13px] text-red-600" data-testid="booking-error-date">{errors.date}</p>}
    </div>
  </div>
);

export const StepContact = ({ d, set, errors }) => (
  <div className="grid sm:grid-cols-2 gap-x-8 gap-y-8">
    <Field label="Name" name="name" value={d.name} onChange={set} error={errors.name} placeholder="Vor- und Nachname" required autoComplete="name" />
    <Field label="Telefon" name="phone" value={d.phone} onChange={set} error={errors.phone} placeholder="+41 79 000 00 00" required type="tel" inputMode="tel" autoComplete="tel" />
    <div className="sm:col-span-2">
      <Field label="E-Mail" name="email" value={d.email} onChange={set} error={errors.email} placeholder="name@beispiel.ch" required type="email" inputMode="email" autoComplete="email" />
    </div>
    <label className="sm:col-span-2 flex items-start gap-3 cursor-pointer">
      <input type="checkbox" checked={d.privacy} onChange={(e) => set("privacy", e.target.checked)} data-testid="booking-privacy-checkbox" className="mt-1 h-5 w-5 accent-[#7c3aed]" />
      <span className="text-[14px] text-[color:var(--ink-2)]">Ich bin einverstanden, dass meine Angaben zur Bearbeitung der Terminanfrage verwendet werden. <a href="/datenschutz" className="underline underline-offset-2">Datenschutz</a></span>
    </label>
    {errors.privacy && <p role="alert" className="sm:col-span-2 -mt-4 text-[13px] text-red-600" data-testid="booking-error-privacy">{errors.privacy}</p>}
  </div>
);

export const StepSummary = ({ d, files, goTo }) => {
  const rows = [
    { l: "Fahrzeug", v: [d.brand, d.model, d.year && `(${d.year})`].filter(Boolean).join(" "), s: 0, sub: [d.mileage && `${d.mileage} km`, d.plate].filter(Boolean).join(" · ") },
    { l: "Service", v: d.service, s: 1, sub: d.message },
    { l: "Wunschtermin", v: `${formatCH(d.date)}${d.time ? `, ${d.time}` : ""}`, s: 3, sub: "Wird von uns persönlich bestätigt" },
    { l: "Kontakt", v: d.name, s: 4, sub: `${d.phone} · ${d.email}` },
  ];
  return (
    <div className="border-t hairline" data-testid="booking-summary">
      {rows.map((r) => (
        <div key={r.l} className="grid sm:grid-cols-[160px_1fr_auto] gap-2 sm:gap-8 py-6 border-b hairline items-start">
          <p className="eyebrow pt-1.5">{r.l}</p>
          <div>
            <p className="text-[20px] font-medium tracking-[-0.015em]" data-testid={`summary-${r.l.toLowerCase()}`}>{r.v}</p>
            {r.sub && <p className="mt-1 text-[15px] text-[color:var(--ink-2)] whitespace-pre-line">{r.sub}</p>}
            {r.l === "Service" && files.length > 0 && <p className="mt-1 text-[13px] text-[color:var(--ink-3)]">{files.length} Foto{files.length > 1 ? "s" : ""} angehängt</p>}
          </div>
          <button type="button" onClick={() => goTo(r.s)} data-testid={`summary-edit-${r.s}`} className="text-[14px] text-[color:var(--ink-2)] hover:text-[color:var(--violet)] link-underline justify-self-start sm:justify-self-end">Ändern</button>
        </div>
      ))}
    </div>
  );
};
